import React from 'react';
import { Scan, Calendar, Building2, User, HardDrive } from 'lucide-react';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import { documentApi } from '../../services/api';
import type { DicomStudy } from '../../types';

interface DicomStudyCardProps {
  study: DicomStudy;
  onClick: () => void;
}

const MODALITY_COLORS: Record<string, string> = {
  CT: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  MR: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  CR: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  DX: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  US: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  NM: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  PT: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  XA: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  MG: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const DicomStudyCard: React.FC<DicomStudyCardProps> = ({ study, onClick }) => {
  const thumbnailUrl = documentApi.getDicomThumbnailUrl(study.id);
  const { blobUrl: thumbnailSrc, containerRef } = useAuthenticatedImage(thumbnailUrl);

  const modalityColor = MODALITY_COLORS[study.modality] || 'bg-gray-100 text-gray-800 dark:bg-medical-600 dark:text-medical-200';

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      className="border border-medical-200 dark:border-medical-600 rounded-lg p-3 hover:border-cyan-400 dark:hover:border-cyan-500 hover:shadow-md cursor-pointer transition-all bg-white dark:bg-medical-800"
    >
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div className="w-16 h-16 flex-shrink-0 bg-gray-900 rounded overflow-hidden flex items-center justify-center">
          {thumbnailSrc ? (
            <img src={thumbnailSrc} alt="DICOM thumbnail" className="w-full h-full object-cover" />
          ) : (
            <Scan className="w-6 h-6 text-gray-500" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${modalityColor}`}>
              {study.modality}
            </span>
            <span className="text-xs text-gray-500 dark:text-medical-400">
              {study.instanceCount} {study.instanceCount === 1 ? 'image' : 'images'}
            </span>
          </div>

          <p className="text-sm font-medium text-gray-900 dark:text-medical-100 truncate">
            {study.llmDescription || study.studyDescription || study.bodyPartExamined || 'DICOM Study'}
          </p>

          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-gray-500 dark:text-medical-400">
            {study.studyDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {study.studyDate}
              </span>
            )}
            {study.institutionName && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {study.institutionName}
              </span>
            )}
            {study.patientName && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {study.patientName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <HardDrive className="w-3 h-3" />
              {formatFileSize(study.totalFileSize)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
